import { useEffect, useRef } from "react";
import { usePortalState } from "~/utils/states";
import { usePortalHydration } from "./usePortalHydration";

export const usePortalAuth = () => {
	const hydrated = usePortalHydration();
	const { isAuthenticated, authToken } = usePortalState();
	const hasInitialCheckRun = useRef(false);

	useEffect(() => {
		if (!hydrated || !isAuthenticated || !authToken) {
			// Reset the flag when not authenticated so it can run again on next login
			hasInitialCheckRun.current = false;
			return;
		}

		// Get functions from state directly to avoid dependency issues
		const { isTokenExpired, refreshToken, logout } = usePortalState.getState();

		// Check token expiry every 2 minutes (more aggressive for better UX)
		const interval = setInterval(
			async () => {
				const {
					tokenExpiry,
					isTokenExpired: currentIsExpired,
					refreshToken: currentRefresh,
					logout: currentLogout,
				} = usePortalState.getState();
				if (!tokenExpiry) return;

				const now = Date.now();
				const timeLeft = tokenExpiry - now;
				const minutesLeft = timeLeft / (1000 * 60);

				// Refresh if expired or less than 5 minutes left
				if (currentIsExpired() || minutesLeft < 5) {
					const refreshed = await currentRefresh();
					if (!refreshed) {
						currentLogout();
					}
				}
			},
			2 * 60 * 1000,
		); // 2 minutes

		// Initial check when hook mounts - prevent duplicate calls
		if (!hasInitialCheckRun.current) {
			hasInitialCheckRun.current = true;

			if (isTokenExpired()) {
				// Token is expired, try refresh or logout
				refreshToken().catch(() => {
					logout();
				});
			} else {
				// Token is still valid, but refresh to extend session (recommended for active users)
				refreshToken().catch(() => {
					// Don't logout if current token is still valid
				});
			}
		}

		return () => clearInterval(interval);
	}, [hydrated, isAuthenticated, authToken]);

	const { isTokenExpired, refreshToken } = usePortalState();

	return {
		isAuthenticated,
		authToken,
		isTokenExpired: isTokenExpired(),
		refreshToken, // Expose refresh function for manual trigger
	};
};
